import { useAppDispatch } from '../store/hooks/reduxHooks';
import { clearAuth } from '../store/slices/authSlice';

// Constants
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_TIMEOUT = 1 * 60 * 1000; // 1 minute before timeout
const SESSION_HEARTBEAT_KEY = 'session_heartbeat';
const HEARTBEAT_INTERVAL = 3000; // 3 seconds
const HEARTBEAT_EXPIRY = 5000; // 5 seconds - should be greater than interval

// Event for warning before timeout
export const SESSION_WARNING_EVENT = 'session-warning';
export const SESSION_CONTINUE_EVENT = 'session-continue';

// Utility function to check and debug session heartbeat
export const checkSessionHeartbeat = (): {
  exists: boolean;
  timestamp: number | null;
  isValid: boolean;
  expiryTime: number;
} => {
  const heartbeat = localStorage.getItem(SESSION_HEARTBEAT_KEY);
  
  if (!heartbeat) {
    return {
      exists: false,
      timestamp: null,
      isValid: false,
      expiryTime: HEARTBEAT_EXPIRY
    };
  }
  
  const timestamp = parseInt(heartbeat, 10);
  const currentTime = Date.now();
  const timeSinceHeartbeat = currentTime - timestamp;
  const isValid = timeSinceHeartbeat <= HEARTBEAT_EXPIRY;
  
  return {
    exists: true,
    timestamp,
    isValid,
    expiryTime: HEARTBEAT_EXPIRY
  };
};

// Function to manually refresh the heartbeat
export const refreshHeartbeat = (): void => {
  localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
  console.log('Session heartbeat manually refreshed');
};

// Function to clear all storage
export const clearAllStorage = () => {
  // Clear localStorage
  localStorage.clear();
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // Clear cookies
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  // Clear service worker cache if available
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }
};

// Function to handle logout
export const handleLogout = (dispatch: ReturnType<typeof useAppDispatch>) => {
  // Clear all storage
  clearAllStorage();
  
  // Clear Redux auth state
  dispatch(clearAuth());
  
  // Redirect to login page
  window.location.href = '/login';
};

// Function to setup inactivity timer
export const setupInactivityTimer = (dispatch: ReturnType<typeof useAppDispatch>) => {
  let inactivityTimer: NodeJS.Timeout;
  let warningTimer: NodeJS.Timeout;
  let isWindowVisible = true;
  let isWarningActive = false;
  
  // Create a custom event for session warning
  const warningEvent = new CustomEvent(SESSION_WARNING_EVENT, {
    detail: {
      warningTimeMS: WARNING_BEFORE_TIMEOUT,
      logoutTimeMS: INACTIVITY_TIMEOUT
    }
  });
  
  // Create a custom event for session continuation
  const continueEvent = new CustomEvent(SESSION_CONTINUE_EVENT);
  
  // Track window visibility
  const handleVisibilityChange = () => {
    isWindowVisible = !document.hidden;
    if (isWindowVisible) {
      resetTimer();
    }
  };
  
  // Reset all timers
  const resetTimer = () => {
    // Only reset timers if there's no active warning
    if (!isWarningActive) {
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
      
      if (isWindowVisible) {
        // Set timer for warning
        warningTimer = setTimeout(() => {
          isWarningActive = true;
          window.dispatchEvent(warningEvent);
        }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);
        
        // Set timer for logout
        inactivityTimer = setTimeout(() => {
          // Only logout if warning is still active
          if (isWarningActive) {
            handleLogout(dispatch);
          }
        }, INACTIVITY_TIMEOUT);
      }
    }
  };
  
  // Function to explicitly continue session
  const continueSession = () => {
    isWarningActive = false;
    window.dispatchEvent(continueEvent);
    resetTimer();
  };
  
  // Function to handle user activity
  const handleUserActivity = () => {
    // If a warning is active, don't auto-dismiss it based on activity
    // User must explicitly continue by clicking the button
    if (!isWarningActive) {
      resetTimer();
    }
  };
  
  // Reset timer on user activity
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  events.forEach((event) => {
    window.addEventListener(event, handleUserActivity);
  });
  
  // Listen for continue session event from the modal
  window.addEventListener(SESSION_CONTINUE_EVENT, continueSession);
  
  // Add visibility change listener
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Start the timer
  resetTimer();
  
  // Return cleanup function
  return () => {
    clearTimeout(inactivityTimer);
    clearTimeout(warningTimer);
    
    events.forEach((event) => {
      window.removeEventListener(event, handleUserActivity);
    });
    
    window.removeEventListener(SESSION_CONTINUE_EVENT, continueSession);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

// Function to setup beforeunload handler
export const setupBeforeUnloadHandler = (dispatch: ReturnType<typeof useAppDispatch>) => {
  // Use a timestamp-based approach that's more reliable for tab closure detection
  
  // Set up a continuous heartbeat to indicate the tab is alive
  const heartbeatInterval = setInterval(() => {
    localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
  }, HEARTBEAT_INTERVAL);
  
  // Check if we should logout on startup
  const checkIfShouldLogout = () => {
    const lastHeartbeat = localStorage.getItem(SESSION_HEARTBEAT_KEY);
    
    // If there's no heartbeat, this is a fresh session
    if (!lastHeartbeat) {
      localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
      return;
    }
    
    const lastHeartbeatTime = parseInt(lastHeartbeat, 10);
    const currentTime = Date.now();
    
    // If the last heartbeat is older than the expiry time, 
    // it means the tab was closed (and not just refreshed)
    if (currentTime - lastHeartbeatTime > HEARTBEAT_EXPIRY) {
      console.log('Tab was closed - logging out');
      // Clear the heartbeat first to prevent race conditions
      localStorage.removeItem(SESSION_HEARTBEAT_KEY);
      // Then logout
      handleLogout(dispatch);
    } else {
      // This was just a refresh, update the heartbeat
      localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
    }
  };
  
  // Add a storage event listener to detect changes from other tabs
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === SESSION_HEARTBEAT_KEY && e.newValue === null) {
      // Another tab has triggered a logout due to timeout
      handleLogout(dispatch);
    }
  };
  
  // Listen for storage events from other tabs
  window.addEventListener('storage', handleStorageChange);
  
  // Check for session status immediately
  checkIfShouldLogout();
  
  // Traditional beforeunload handler as fallback
  let isRefreshing = false;
  
  window.addEventListener('beforeunload', () => {
    isRefreshing = true;
    // We'll let the heartbeat expiration handle the logout now
  });
  
  window.addEventListener('unload', () => {
    if (!isRefreshing) {
      // Direct tab close - ensure heartbeat stops immediately
      localStorage.removeItem(SESSION_HEARTBEAT_KEY);
    }
  });
  
  // Return cleanup function
  return () => {
    clearInterval(heartbeatInterval);
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener('unload', () => {});
    window.removeEventListener('beforeunload', () => {});
  };
}; 